import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { SsoButton } from "@/components/SsoButton";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "../lib/api";
import { resolveIcon } from "../lib/icons";
import { useSession } from "../lib/useSession";

interface AppTile {
  id: string;
  slug: string;
  name: string;
  description: string;
  businessUnit: "stickers" | "figures" | "electronics" | "channels" | "platform";
  status: "live" | "beta" | "planned" | "retired";
  url: string;
  icon: string;
}

const UNIT_ORDER = ["stickers", "figures", "electronics", "channels", "platform"] as const;

const STATUS_VARIANT: Record<AppTile["status"], "success" | "warning" | "secondary"> = {
  live: "success",
  beta: "warning",
  planned: "secondary",
  retired: "secondary",
};

export default function Home() {
  const { t } = useTranslation();
  const { user, isLoading: sessionLoading } = useSession();

  const { data: apps, isLoading } = useQuery({
    queryKey: ["apps"],
    queryFn: () => api<AppTile[]>("/api/v1/apps"),
    enabled: Boolean(user),
  });

  if (sessionLoading) return <p className="text-muted-foreground">{t("common:loading")}</p>;

  if (!user) {
    return (
      <section className="mx-auto max-w-md py-8">
        <Card>
          <CardHeader className="text-center">
            <CardTitle asChild className="text-3xl">
              <h1>{t("common:appName")}</h1>
            </CardTitle>
            <CardDescription>{t("auth:signInPrompt")}</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <SsoButton className="w-full" />
          </CardContent>
        </Card>
      </section>
    );
  }

  const grouped = UNIT_ORDER.map((unit) => ({
    unit,
    items: (apps ?? []).filter((a) => a.businessUnit === unit),
  })).filter((g) => g.items.length > 0);

  return (
    <>
      <div className="mb-6">
        <h1>{t("common:welcome", { name: user.displayName })}</h1>
        <p className="mt-2 text-muted-foreground">{t("common:launcherSubtitle")}</p>
      </div>

      {isLoading && <p className="text-muted-foreground">{t("common:loading")}</p>}

      {!isLoading && grouped.length === 0 && (
        <p className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          {t("common:noApps")}
        </p>
      )}

      {grouped.map(({ unit, items }) => (
        <section key={unit} className="mb-8">
          <h2 className="mb-3">{t(`common:units.${unit}`)}</h2>
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((app) => {
              const Icon = resolveIcon(app.icon);
              return (
                <li key={app.id}>
                  <a href={app.url} className="tap block h-full">
                    <Card className="h-full transition-colors hover:border-ring">
                      <CardHeader className="p-4 pb-2">
                        <CardTitle className="flex items-center gap-2 text-base">
                          <Icon className="h-5 w-5 shrink-0" aria-hidden />
                          {app.name}
                          <Badge variant={STATUS_VARIANT[app.status]} className="ml-auto">
                            {t(`common:status.${app.status}`)}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 pt-0">
                        <CardDescription>{app.description}</CardDescription>
                      </CardContent>
                    </Card>
                  </a>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </>
  );
}
